// SP: page counter for the Latest Restocks carousel — "1 | 18" between the
// prev/next circles. Figma node 1916:27659 (page-context render) draws the
// pagination group as 211x42: a 42px outlined prev circle, the current page,
// a 1px x 21px rule, the total, then a 42px filled next circle, the whole
// group centred on the PAGE (its centre lands on 960 in a 1920 frame).
//
// Injected rather than added to `sections/featured-collection.liquid`:
// vendor files are read-only unless there is no alternative (THEME-CHANGES.md
// working rule 1), and the numbers have to update on scroll anyway, so markup
// alone could not have delivered this. Styling lives in sp-restocks.css.
//
// The counter is inserted INTO `.hometitle`, between the two buttons. That
// element is `display: contents` in this section, so its children are real
// grid items of `.collection-slider` — the counter lands in its own grid
// column with no wrapper box of its own. `carousel-slider` delegates nav
// clicks with `evt.target.matches('.slider-nav__btn')` (assets/slider.js
// ~219), so a non-button child between them is inert, and `refresh()`
// re-queries only `.slider__item`, so this element is never counted as a
// slide.
//
// The index/total math deliberately MIRRORS the vendor's own (slider.js
// initSlider) rather than reading `carousel-slider`'s instance properties:
// the element initialises through `initLazyScript`, so those properties do
// not exist yet at our first paint, and duplicating four lines of arithmetic
// is cheaper than racing its init. Mirroring it also keeps the counter in
// agreement with the prev/next disabled states, which are derived the same way.
(function () {
  var SECTION_SUFFIX = '__16359278676808bea5';

  function build() {
    var el = document.createElement('div');
    el.className = 'sp-paging-count';
    var current = document.createElement('span');
    current.className = 'sp-paging-count__current';
    var sep = document.createElement('span');
    sep.className = 'sp-paging-count__sep';
    sep.setAttribute('aria-hidden', 'true');
    var total = document.createElement('span');
    total.className = 'sp-paging-count__total';
    el.appendChild(current);
    el.appendChild(sep);
    el.appendChild(total);
    return { el: el, current: current, total: total };
  }

  function init(section) {
    if (!section || section.hasAttribute('data-sp-paging')) return;

    var carousel = section.querySelector('carousel-slider');
    var nav = section.querySelector('.hometitle.has-paging');
    if (!carousel || !nav) return;

    var nextBtn = nav.querySelector('button[name="next"]');
    var scroller = carousel.querySelector('.slider');
    var grid = carousel.querySelector('.slider__grid');
    if (!nextBtn || !scroller || !grid) return;

    section.setAttribute('data-sp-paging', '');

    var parts = build();
    nav.insertBefore(parts.el, nextBtn);

    // Geometry, re-read every time: card width and therefore how many fit
    // changes at every breakpoint, and lazy images shift offsetLeft after
    // first layout.
    function geom() {
      var slides = carousel.querySelectorAll('.slider__item:not([hidden])');
      if (slides.length < 2) return null;

      // Leading-edge distance between adjacent slides (card + gap).
      var span = slides[1].offsetLeft - slides[0].offsetLeft;
      if (!span) return null; // pre-layout, or a single visible column

      var gap = span - slides[0].clientWidth;

      // FLOOR, not round: this section deliberately shows a FRACTIONAL number
      // of cards (2.49 at 390, 3.92 at 756, 5.68 at 1440, 5.71 at 1920 —
      // affordance that says the row scrolls. Rounding 5.68 up to 6 and
      // stepping by that would scroll the peeked card straight off the left
      // edge, so it would never once be seen whole — the step would skip
      // exactly the card the peek was advertising. Flooring makes the peeked
      // card the first fully-visible card of the next page, which is what
      // "the next batch, in clean view" means.
      //
      // inside the scroller at every width (724 vs 756 at 756px, 1840 vs 1880 at
      // 1920), so sizing the page from it understates what is on screen: at
      // 756 it gives 3.92 -> 3 while four cards genuinely fit (4 x 187 - 9 =
      // 739 inside 756), and stepping 3 left every page overlapping the last by
      // one card. Confirmed by real Input clicks: pages read 1-4, 4-7, 7-10
      // before this line used the scroller, and 1-4, 5-8, 9-11 after.
      var whole = Math.max(1, Math.floor((scroller.clientWidth + gap) / span));
      return { slides: slides, n: slides.length, span: span, gap: gap, whole: whole };
    }

    function maxScroll() {
      return scroller.scrollWidth - scroller.clientWidth;
    }

    function measure() {
      var g = geom();
      if (!g) return null;

      var max = maxScroll();
      // Pages, not a sliding window. The vendor's own total counts every
      // one-card position (slides.length - perPage + 1 = 9 of 11 cards at
      // three-up), which is the total for a control that steps one card at a
      // time. Stepping by the page makes that total wrong, so it is computed
      // here instead: the first page plus one for each further whole step.
      var total = Math.max(1, Math.ceil((g.n - g.whole) / g.whole) + 1);
      var pageSpan = g.whole * g.span;

      // The last page is CLAMPED to max scroll rather than sitting at
      // (total-1) * pageSpan, which usually overshoots the end. So position
      // cannot be derived from arithmetic alone at that one stop — being at
      // max IS being on the last page, by definition.
      var index;
      if (max > 0 && scroller.scrollLeft >= max - 1) index = total - 1;
      else index = Math.round(scroller.scrollLeft / pageSpan);
      if (index < 0) index = 0;
      if (index > total - 1) index = total - 1;
      return { current: index + 1, total: total, index: index, pageSpan: pageSpan, max: max };
    }

    function update() {
      var m = measure();
      // Hide rather than show a bogus "1 | 1": the vendor drops the whole nav
      // when there is nothing to page through (setCarouselState), and a lone
      // counter under a static row would read as broken.
      if (!m || m.total < 2) {
        parts.el.hidden = true;
        return;
      }
      parts.el.hidden = false;
      parts.current.textContent = m.current;
      parts.total.textContent = m.total;
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    scroller.addEventListener('scroll', onScroll, { passive: true });

    // ---- Paging ---------------------------------------------------------
    // The vendor steps by `slidesToScroll * slideSpan` (assets/slider.js
    // 756, 1100, 1440 and 1920, all 1. That is a THEME-WIDE setting, not this
    // section's: slider.js:58 reads
    //   `theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1`
    // so flipping it to page would change every carousel in the theme, and it
    // lives in settings_data.json where the theme editor also writes. Taking
    // the clicks over here keeps the blast radius to this one section.
    //
    // batch thats in clean view." One card per click also made the counter
    // lie — it read "1 | 9" while a click moved the row by a third of a
    // screenful.
    //
    // Taken over in the CAPTURE phase on the buttons themselves: the vendor
    // delegates from `this.nav` (slider.js:105), so stopPropagation here keeps
    // its handler from also running and fighting this scroll. Everything else
    // it does still works, because it is driven by scroll events rather than
    // by the click — setButtonStates reads scrollLeft and slide visibility
    // (slider.js:301), so prev still disables at 0 and next still disables
    // once the last card is fully visible.
    var prevBtn = nav.querySelector('button[name="prev"]');

    function goToPage(index) {
      var m = measure();
      if (!m) return;
      var i = Math.min(Math.max(0, index), m.total - 1);
      // Last page goes to the true end, so the final card lands flush with the
      // right edge and the remainder falls on the left. Anything else leaves
      // the strip stopped mid-card with no way to reach the end.
      var target = i === m.total - 1 ? m.max : Math.min(i * m.pageSpan, m.max);
      scroller.scrollTo({ left: target, behavior: 'smooth' });
    }

    function takeOver(btn, delta) {
      if (!btn) return;
      btn.addEventListener('click', function (evt) {
        var m = measure();
        if (!m || m.max <= 0) return; // nothing to scroll; let the vendor be
        evt.stopPropagation();
        evt.preventDefault();
        goToPage(m.index + delta);
      }, true);
    }

    takeOver(nextBtn, 1);
    takeOver(prevBtn, -1);

    // Card width, and therefore slides-per-page, changes at every breakpoint.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(onScroll).observe(grid);
    } else {
      window.addEventListener('resize', onScroll);
    }

    update();
    // Fonts and lazy images can settle after first layout and shift offsetLeft.
    requestAnimationFrame(update);
    window.addEventListener('load', update);
  }

  function boot() {
    // Every section wearing the restock look gets a counter: the Latest
    // restock row by its key, any sp-shop-favorites by its class.
    document.querySelectorAll('[id$="' + SECTION_SUFFIX + '"], .sp-restock-look').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the section in place.
  document.addEventListener('shopify:section:load', function (evt) {
    var t = evt.target;
    if (t && ((t.id && t.id.indexOf(SECTION_SUFFIX) !== -1) || (t.classList && t.classList.contains('sp-restock-look')))) {
      t.removeAttribute('data-sp-paging');
      init(t);
    }
  });
})();
